/**
 * @file api.ts
 * @brief TanStack Query hooks for the Super Admin → Feedback feature.
 *
 * @details
 * Read-only surfaces over `/api/feedback/*` (super-admin gated on the
 * backend). Responses are parsed against Zod schemas before use.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client   (apiGet)
 * - zod
 * - shared/api/endpoints
 * - features/feedback/schemas
 *
 * @references
 * - BACKEND/src/features/feedback/routes.js
 *
 * @author DES
 * @date 05-07-2026
 */

import {
  useQueries,
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "@/shared/api/client";
import { feedbackEndpoints } from "@/shared/api/endpoints";
import {
  AppFeedbackListResponseSchema,
  FeedbackUserGroupSchema,
  SessionFeedbackListResponseSchema,
  type AppFeedbackListParams,
  type AppFeedbackListResponse,
  type FeedbackUserGroup,
  type SessionFeedbackListParams,
  type SessionFeedbackListResponse,
} from "./schemas";

export const feedbackKeys = {
  all: ["admin", "feedback"] as const,
  sessions: (params: SessionFeedbackListParams) =>
    ["admin", "feedback", "sessions", params] as const,
  app: (params: AppFeedbackListParams) =>
    ["admin", "feedback", "app", params] as const,
};

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).length > 0) {
      usp.set(k, String(v));
    }
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function fetchSessionFeedbackPage(
  params: SessionFeedbackListParams,
): Promise<SessionFeedbackListResponse> {
  const url = `${feedbackEndpoints.sessions}${buildQuery({
    page: params.page,
    pageSize: params.pageSize,
    type: params.type,
    minRating: params.minRating,
    from: params.from,
    to: params.to,
    search: params.search,
    user: params.user,
  })}`;
  const raw = await apiGet<unknown>(url);
  return SessionFeedbackListResponseSchema.parse(raw);
}

async function fetchAppFeedbackPage(
  params: AppFeedbackListParams,
): Promise<AppFeedbackListResponse> {
  const url = `${feedbackEndpoints.app}${buildQuery({
    page: params.page,
    pageSize: params.pageSize,
    from: params.from,
    to: params.to,
    search: params.search,
    user: params.user,
  })}`;
  const raw = await apiGet<unknown>(url);
  return AppFeedbackListResponseSchema.parse(raw);
}

export function useSessionFeedbackList(
  params: SessionFeedbackListParams,
  options?: Partial<UseQueryOptions<SessionFeedbackListResponse>>,
) {
  return useQuery<SessionFeedbackListResponse>({
    queryKey: feedbackKeys.sessions(params),
    queryFn: () => fetchSessionFeedbackPage(params),
    placeholderData: (prev) => prev,
    ...options,
  });
}

export function useAppFeedbackList(
  params: AppFeedbackListParams,
  options?: Partial<UseQueryOptions<AppFeedbackListResponse>>,
) {
  return useQuery<AppFeedbackListResponse>({
    queryKey: feedbackKeys.app(params),
    queryFn: () => fetchAppFeedbackPage(params),
    placeholderData: (prev) => prev,
    ...options,
  });
}

// ----------------------------------------------------------------------
// Server-backed user groups (DES 01-10-2026: "haan superadmin pages pe bhi
// yehi kar do"). The page used to fetch every row and group it in the
// browser; now the server returns each user with their count, and a group
// fetches its own rows when opened, 25 at a time.
// ----------------------------------------------------------------------

/** The group key for a user; `none` for feedback with no user. */
export const feedbackUserKey = (userId: string | null | undefined) => userId || "none";

/** Rows per batch when a group opens or loads more. */
export const FEEDBACK_GROUP_BATCH = 25;

type FeedbackKind = "session" | "app";
type ParamsOf<K extends FeedbackKind> = K extends "session"
  ? SessionFeedbackListParams
  : AppFeedbackListParams;
type ResponseOf<K extends FeedbackKind> = K extends "session"
  ? SessionFeedbackListResponse
  : AppFeedbackListResponse;

/** The list filters without paging or user (shared by the grouped calls). */
function filterQuery(
  kind: FeedbackKind,
  params: SessionFeedbackListParams | AppFeedbackListParams,
): URLSearchParams {
  const p = params as SessionFeedbackListParams;
  const usp = new URLSearchParams(
    buildQuery({
      ...(kind === "session" ? { type: p.type, minRating: p.minRating } : {}),
      from: p.from,
      to: p.to,
      search: p.search,
    }).replace(/^\?/, ""),
  );
  return usp;
}

const endpointOf = (kind: FeedbackKind) =>
  kind === "session" ? feedbackEndpoints.sessions : feedbackEndpoints.app;

/** Every user with feedback under the current filters, with counts. */
export function useFeedbackGroupSummary<K extends FeedbackKind>(
  kind: K,
  params: ParamsOf<K>,
  enabled: boolean,
) {
  return useQuery<FeedbackUserGroup[]>({
    queryKey: [...feedbackKeys.all, kind, "groups", filterQuery(kind, params).toString()],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = filterQuery(kind, params);
      usp.set("group", "user");
      const raw = await apiGet<{ groups?: unknown }>(`${endpointOf(kind)}?${usp}`);
      return z.array(FeedbackUserGroupSchema).parse(raw.groups ?? []);
    },
  });
}

/**
 * The rows of each OPEN group, `batches` pages of FEEDBACK_GROUP_BATCH,
 * under the same filters (`?user=`). The server caps `pageSize` at 100, so
 * the pages are fetched and joined rather than asking for one big page.
 */
export function useFeedbackGroupRows<K extends FeedbackKind>(
  kind: K,
  params: ParamsOf<K>,
  open: { key: string; batches: number }[],
) {
  const filters = filterQuery(kind, params).toString();
  return useQueries({
    queries: open.map(({ key, batches }) => ({
      queryKey: [...feedbackKeys.all, kind, "group", key, batches, filters],
      placeholderData: (prev: ResponseOf<K> | undefined) => prev,
      queryFn: async (): Promise<ResponseOf<K>> => {
        const pages = await Promise.all(
          Array.from({ length: batches }, (_, i) => {
            const pageParams = {
              ...params,
              user: key,
              page: i + 1,
              pageSize: FEEDBACK_GROUP_BATCH,
            };
            return (
              kind === "session"
                ? fetchSessionFeedbackPage(pageParams as SessionFeedbackListParams)
                : fetchAppFeedbackPage(pageParams as AppFeedbackListParams)
            ) as Promise<ResponseOf<K>>;
          }),
        );
        return {
          ...pages[0],
          items: pages.flatMap((p) => p.items as unknown[]),
        } as ResponseOf<K>;
      },
    })),
  });
}
